import { connectToParent } from '../../../../shared/app/vendor/penpal';
import { ucFirst } from '../../../../shared/app/utils/string-utils';
import { noop } from '../../../../shared/app/utils/function-utils';
import { toSafeObject } from '../../../../shared/app/utils/object-utils/object-utils';
import { appAlertComponentModule } from '../../../../shared/app/components/app-alert/app-alert';
import { isFunction, isObject } from '../../../../shared/app/utils/type-utils';
import { appConfig } from '../../../../shared/app/constants/app-config';
import { hostname } from '../../../../shared/app/utils/dom-utils';
import { embedded } from '../../config/embedded';
import { ERROR_CONTEXT } from '../../../../embed/app/manager/assessment-options';
import { toISOString } from '../../../../shared/app/utils/date-utils';

// Yes, you really have to import and export separately like this. Exporting does
// _not_ import the module into the current context.
export { ERROR_CONTEXT } from '../../../../embed/app/manager/assessment-options';

export const messageHubServiceModule = angular
  .module('Q.services.message-hub-service', [appAlertComponentModule])
  .constant('embedOptions', {})
  .factory(
    'messageHubService',
    function (
      $window,
      $document,
      appAlert,
      embedOptions,
      $rootScope,
      $q,
      $http,
      authInterceptor,
    ) {
      const readyDeferred = $q.defer();

      const svc = {
        authorize,
        postError,
        postLoaded,
        postLoadError,
        postUpdated,
        postSolutionUpdated,
        postSubmitted,
        embedded,
        onSetOptions(options) {
          angular.extend(embedOptions, options);
          if (options.authToken) {
            authInterceptor.setAuthToken(options.authToken);
          }
          // we're finally ready the first time we get options
          readyDeferred.resolve(svc);
          $rootScope.$applyAsync();
        },
        whenReady() {
          return readyDeferred.promise;
        },
      };

      const methods = {};
      ['setOptions', 'next', 'previous', 'switchChallenge', 'submit'].forEach(
        (method) => {
          // wrap the event handler so we can call the method on messageHubService after it gets set
          methods[method] = (...args) => {
            const handler = `on${ucFirst(method)}`;
            return svc[handler] && svc[handler](...args);
          };
        },
      );

      let parent;

      const parentCheck = (fn) => parent && isFunction(parent[fn]);

      let connection = { destroy: noop };

      $q.resolve()
        .then(() => {
          if (embedded) {
            connection = connectToParent({
              timeout: 10000,
              methods,
            });
            return connection.promise;
          } else {
            return null;
          }
        })
        .then((_parent) => {
          parent = _parent;
          if (parent) {
            // Notify the outer page that the page is loaded
            parent.ready();
          } else {
            // nothing to wait for
            readyDeferred.resolve(svc);
          }
        })
        .catch((err) => {
          readyDeferred.resolve(svc);
          if (err && !this._destroyed) {
            appAlert.warning('Error connecting iframe:', err);
          }
        });

      return svc;

      function authorize(assessment) {
        return readyDeferred.promise
          .then(
            () =>
              embedded &&
              $http.post(
                `${appConfig.apiRoot}embed_assessment/${assessment.id}`,
                {
                  embedClientKey: embedOptions.embedClientKey,
                  embedDomain: hostname($document[0].referrer),
                },
              ),
          )
          .then(() => assessment)
          .catch((err) => {
            let errData = err;
            if (err && err.data) {
              errData = err.data;
              errData.status = err.status;
              errData.statusText = err.statusText;
            }
            return $q.reject(errData);
          });
      }

      function _loadData(assessment, assessmentResult, challenge, stateInfo) {
        const stageId = challenge
          ? challenge.id
          : stateInfo
            ? stateInfo.id
            : undefined;
        const challengeItems = assessmentResult
          ? assessmentResult.getChallengeItems()
          : assessment.challengeItems;
        const stageIndex = stateInfo
          ? stateInfo.index
          : 1 + challengeItems.findIndex((ci) => ci.challengeId === stageId);
        return {
          assessmentId: assessment.id,
          title: assessment.title,
          summary: assessment.summary,
          assessmentResultId: assessmentResult ? assessmentResult.id : null,
          started: !!assessmentResult && assessmentResult.isActive(),
          submitted: !!assessmentResult && assessmentResult.isSubmitted(),
          startedAt: toISOString(
            assessmentResult && assessmentResult.startedAt,
          ),
          timeLimit: assessmentResult ? assessmentResult.timeLimit() : null,
          cutOffTime: assessmentResult ? assessmentResult.cutOffTime() : null,
          score: assessmentResult ? assessmentResult.score : 0,
          challenges: challengeItems.map((ci) =>
            _challengeData(ci.challenge, assessmentResult),
          ),
          stageId,
          stageIndex,
        };
      }

      function _challengeData(challenge, assessmentResult) {
        const data = {
          id: null,
        };
        if (challenge) {
          data.id = challenge.id;
          data.index =
            assessmentResult &&
            1 +
              assessmentResult
                .getChallengeItems()
                .findIndex((ci) => ci.challengeId === challenge.id);
          data.title = challenge.title;
          data.type = challenge.$type;
          data.summary = challenge.summary;
          const sol =
            assessmentResult &&
            assessmentResult.solutions.find(
              (sol) => sol.challengeId === challenge.id,
            );
          data.started = !!sol;
          if (sol) {
            data.solutionId = sol.id;
            data.score = sol.score;
            data.solutionLanguage = sol.language;
            data.attempted = !!sol.attempted();
            data.completed = !!sol.passed();
          }
        }
        return data;
      }

      function _cleanUpError(context, msg) {
        let errorData = toSafeObject(msg);
        if (!isObject(errorData)) errorData = { message: errorData };
        errorData.context = context;
        return errorData;
      }

      function postError(context, msg) {
        if (!parentCheck('error')) return;
        parent.error(_cleanUpError(context, msg));
      }

      function postLoaded({ assessment, assessmentResult, challenge }) {
        if (!parentCheck('loaded')) return;
        parent.loaded(
          toSafeObject(_loadData(assessment, assessmentResult, challenge)),
        );
      }

      function postLoadError(msg) {
        postError(ERROR_CONTEXT.load, msg);
        if (!parentCheck('loaded')) return;
        parent.loaded({
          error: _cleanUpError(ERROR_CONTEXT.load, msg),
          started: false,
        });
      }

      function postUpdated({
        assessmentResult,
        assessment = assessmentResult.assessment,
        challenge,
        stateInfo,
      }) {
        if (!parentCheck('updated')) return;
        parent.updated(
          toSafeObject(
            _loadData(assessment, assessmentResult, challenge, stateInfo),
          ),
        );

        // Also always post a solution updated event
        postSolutionUpdated({ assessmentResult, challenge, stateInfo });
      }

      function postSolutionUpdated({ assessmentResult, challenge, stateInfo }) {
        if (!parentCheck('solutionUpdated')) return;
        let data = stateInfo || _challengeData(challenge, assessmentResult);
        parent.solutionUpdated(toSafeObject(data));
      }

      function postSubmitted(assessmentResult) {
        if (!parentCheck('submitted')) return;
        const data = {
          assessmentId: assessmentResult.assessmentId,
          assessmentResultId: assessmentResult.id,
          score: assessmentResult.score,
          challenges: assessmentResult
            .getChallengeItems()
            .map((ci) => _challengeData(ci.challenge, assessmentResult)),
          timerRanOut: assessmentResult.timerRanOut(),
          solutionsStarted: assessmentResult.solutions.length,
          solutionsAttempted: assessmentResult.solutions.filter((sol) =>
            sol.attempted(),
          ).length,
          solutionsCompleted: assessmentResult.solutions.filter((sol) =>
            sol.passed(),
          ).length,
        };
        parent.submitted(toSafeObject(data));
      }
    },
  );
